import { useEffect, useState, createContext, useContext, useCallback, useMemo } from "react";
import api from "./api";
import { useAuth } from "./auth";

const PermissionContext = createContext({ perms: new Set(), loading: true, refresh: () => {} });

export function PermissionProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [perms, setPerms] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/rbac/me/effective");
      setPerms(new Set(r.data?.permissions || []));
    } catch {
      setPerms(new Set());
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (user?.workspace === "pharmacy") {
      setPerms(new Set());
      setLoading(false);
    } else if (user) {
      setLoading(true);
      refresh();
    } else {
      setPerms(new Set());
      setLoading(false);
    }
  }, [user, authLoading, refresh]);

  useEffect(() => {
    if (!user || user.workspace === "pharmacy") return undefined;
    const refreshOnFocus = () => refresh();
    window.addEventListener("focus", refreshOnFocus);
    const intervalId = window.setInterval(refresh, 60_000);
    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      window.clearInterval(intervalId);
    };
  }, [user, refresh]);

  const value = useMemo(() => ({ perms, loading, refresh }), [perms, loading, refresh]);
  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions() {
  return useContext(PermissionContext);
}

/** Returns true if user has all slugs passed (single string or array). Wildcard `*` matches. */
export function useCan(slugs) {
  const { perms } = usePermissions();
  const arr = Array.isArray(slugs) ? slugs : [slugs];
  if (perms.has("*")) return true;
  return arr.every((s) => perms.has(s));
}

/** Returns true if user has ANY of the slugs (single string or array). */
export function useCanAny(slugs) {
  const { perms } = usePermissions();
  const arr = Array.isArray(slugs) ? slugs : [slugs];
  if (perms.has("*")) return true;
  return arr.some((s) => perms.has(s));
}

/** Renders children only if user has perm(s). */
export function IfCan({ perms: required, any = false, fallback = null, children }) {
  const { perms } = usePermissions();
  const arr = Array.isArray(required) ? required : [required];
  const has = perms.has("*") || (any ? arr.some((s) => perms.has(s)) : arr.every((s) => perms.has(s)));
  return has ? <>{children}</> : <>{fallback}</>;
}
